// Localization layer (i18next + react-i18next). Every user-facing string goes
// through `t()`; dates and numbers go through the locale-aware helpers in
// lib/format and lib/time, which read the current language from here.
//
// The language is an ACCOUNT setting shared with Telegram (`account.language`:
// 'ru' | 'en' | 'de'), not a web-only preference: the app follows whatever the
// account says (LanguageSync in the shell), and changing it in Settings writes
// the account. When the ORCA API is connected nothing here changes — it just
// returns `language` with the account.
import i18n from 'i18next';
import { initReactI18next, useTranslation } from 'react-i18next';
import de from './de';
import en from './en';
import ru from './ru';
import { rememberPreloaderLang } from '@/lib/preloader';

export const LANGS = ['ru', 'en', 'de'] as const;
export type Lang = (typeof LANGS)[number];
export const LOCALES: Record<Lang, string> = { ru: 'ru-RU', en: 'en-US', de: 'de-DE' };
export type Ns = keyof typeof en;
export const NAMESPACES = Object.keys(en) as Ns[];

const DEV = import.meta.env.DEV;

void i18n.use(initReactI18next).init({
  resources: { ru, en, de },
  lng: 'ru',
  ns: NAMESPACES,
  defaultNS: 'common',
  // production: a missing key falls back to English; DEV: it is shown loudly (no silent fallback)
  fallbackLng: DEV ? false : 'en',
  interpolation: { escapeValue: false },
  returnNull: false,
  saveMissing: DEV,
  missingKeyHandler: (lngs, ns, key) => { console.error(`[i18n] missing key ${ns}:${key} (${lngs.join(',')})`); },
  parseMissingKeyHandler: DEV ? (key) => `⟦${key}⟧` : undefined,
  react: { bindI18n: 'languageChanged', useSuspense: false },
});

// the first-frame splash (index.html) is painted before i18n loads: it reads the last language from here
i18n.on('languageChanged', (l) => rememberPreloaderLang(l, i18n.t('auth:preloader')));

export const lang = (): Lang => (LANGS.includes(i18n.language as Lang) ? (i18n.language as Lang) : 'ru');
/** BCP 47 locale of the current language for Intl formatters */
export const locale = () => LOCALES[lang()];

// DEV: the three languages must have the same keys (plural forms aside)
if (DEV) {
  const flat = (o: object, p = ''): string[] => Object.entries(o).flatMap(([k, v]) => (v && typeof v === 'object' ? flat(v, `${p}${k}.`) : [`${p}${k}`.replace(/_(zero|one|two|few|many|other)$/, '')]));
  const sets = { ru: new Set(flat(ru)), en: new Set(flat(en)), de: new Set(flat(de)) };
  for (const [l, s] of Object.entries(sets)) {
    const missing = [...sets.en].filter((k) => !s.has(k)).concat([...s].filter((k) => !sets.en.has(k)).map((k) => `(extra) ${k}`));
    if (missing.length) console.error(`[i18n] ${l}: key mismatch with en:`, missing.slice(0, 40));
  }
}

export default i18n;

/**
 * `t` for a component: keys of `ns` without a prefix, any other namespace as
 * `ns:key`. Re-renders the component when the language changes.
 */
export type TFn = (key: string, opts?: Record<string, unknown>) => string;

export function useT(ns?: Ns) {
  const first = ns ?? 'common';
  const r = useTranslation([first, ...NAMESPACES.filter((x) => x !== first)]);
  return { t: r.t as unknown as TFn, i18n: r.i18n };
}

/** A label map read from the dictionary at use time: `CAT_LABEL.macro` → news:cat.macro in the current language. */
export function labels<K extends string>(prefix: string, keys: readonly K[]): Record<K, string> {
  return new Proxy({} as Record<K, string>, {
    // only the map's own keys (React / devtools probe objects with symbols and $$typeof)
    get: (_, k) => (typeof k === 'string' && (keys as readonly string[]).includes(k) ? (i18n.t as unknown as (key: string) => string)(`${prefix}.${k}`) : undefined),
    ownKeys: () => [...keys],
    getOwnPropertyDescriptor: () => ({ enumerable: true, configurable: true }),
  });
}
